import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../../config/api';
import { SESSION_PRICE_INR, SESSION_LIST_PRICE_INR, SESSION_DISCOUNT_PERCENT, SESSION_HAS_DISCOUNT, formatInr } from '../../data/roadmapSession';
import { useAuth } from '../../context/AuthContext';
import CountUp from './CountUp';
import GoogleMark from '../GoogleMark';
import { normalizePhone, PHONE_ERROR, PHONE_ALLOWED } from '../../utils/phone';

const TZ = 'Asia/Kolkata';
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// What the visitor walks away with from the call
const plan = [
  { step: 'Where you stand', title: 'An honest check of your skills against the roles you want' },
  { step: 'CV review', title: 'Line-by-line fixes so recruiters actually shortlist you' },
  { step: 'Your career path', title: 'Exactly what to learn, build and skip, in the right order' }
];

// The scattered spending most learners recognise
const receipt = [
  { item: '"Full-stack in 90 days" bootcamp', note: 'Stopped after week 3', cost: 45000 },
  { item: 'DSA masterclass', note: 'Still stuck on medium problems', cost: 12000 },
  { item: 'Data science certificate', note: 'No interviewer asked about it', cost: 35000 },
  { item: 'System design crash course', note: 'Too early for your level', cost: 8500 },
  { item: 'Weekend AI/ML workshop', note: 'Bought because everyone was', cost: 18000 }
];
const receiptTotal = receipt.reduce((sum, r) => sum + r.cost, 0);
const inr = (n) => n.toLocaleString('en-IN');

const stages = ['College student', 'Fresher / job hunting', 'Working, 0–2 years', 'Working, 2+ years', 'Switching into tech'];

const fmt = (iso, opts) => new Intl.DateTimeFormat('en-IN', { timeZone: TZ, ...opts }).format(new Date(iso));
const timeLabel = (iso) => fmt(iso, { hour: 'numeric', minute: '2-digit', hour12: true });
const longDate = (iso) => fmt(iso, { weekday: 'long', day: 'numeric', month: 'long' });
const istHour = (iso) => Number(fmt(iso, { hour: 'numeric', hour12: false }));

// "2026-10-14" -> Date at UTC midnight, used only to lay out the calendar grid
const keyToDate = (key) => new Date(`${key}T00:00:00Z`);
const dateToKey = (d) => d.toISOString().slice(0, 10);

// Weeks (Mon–Sun) covering every bookable day
const buildWeeks = (days) => {
  if (!days.length) return [];
  const first = keyToDate(days[0].date);
  const last = keyToDate(days[days.length - 1].date);
  const start = new Date(first);
  start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
  const weeks = [];
  for (let d = new Date(start); d <= last || d.getUTCDay() !== 1; d.setUTCDate(d.getUTCDate() + 1)) {
    if (d.getUTCDay() === 1) weeks.push([]);
    weeks[weeks.length - 1].push(dateToKey(d));
  }
  return weeks;
};

const googleCalendarLink = (iso, minutes) => {
  const start = new Date(iso);
  const end = new Date(start.getTime() + minutes * 60000);
  const stamp = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: 'Codeazy – Personal roadmap session',
    dates: `${stamp(start)}/${stamp(end)}`,
    details: 'Your 1:1 roadmap session with a Codeazy mentor. We will share the call link before the session.'
  });
  return `https://calendar.google.com/calendar/render?${params}`;
};

const emptyForm = { name: '', phoneNumber: '', currentStage: '', goal: '' };

// Remembers the chosen date across the Google sign-in redirect
const PENDING_DATE_KEY = 'codeazy:pending-booking-date';
const savePendingDate = (value) => {
  try { window.sessionStorage.setItem(PENDING_DATE_KEY, value); } catch { /* storage unavailable */ }
};
const takePendingDate = () => {
  try {
    const value = window.sessionStorage.getItem(PENDING_DATE_KEY);
    window.sessionStorage.removeItem(PENDING_DATE_KEY);
    return value;
  } catch {
    return null;
  }
};

const fullNameOf = (user) => user?.user_metadata?.full_name || user?.user_metadata?.name || '';

const RoadmapBooking = () => {
  const [schedule, setSchedule] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [dateKey, setDateKey] = useState('');
  const [slot, setSlot] = useState('');
  const [step, setStep] = useState('pick'); // pick | details | done
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [invited, setInvited] = useState(false); // Google sent a calendar invite
  const [redirecting, setRedirecting] = useState(false);
  const { user, loading: authLoading, signInWithGoogle, getAccessToken } = useAuth();
  const sectionRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Back from a successful checkout: show the confirmation once, then clear it
  // from history so a refresh doesn't show it again
  useEffect(() => {
    const confirmed = location.state?.bookingConfirmed;
    if (!confirmed) return;
    setSlot(confirmed.slotStart);
    setInvited(Boolean(confirmed.calendarInvite));
    setStep('done');
    navigate(`${location.pathname}${location.hash}`, { replace: true, state: null });
  }, [location, navigate]);

  const loadSlots = useCallback(async () => {
    setLoadError('');
    try {
      const res = await fetch(`${API_URL}/session-bookings/slots`);
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message);
      setSchedule(json.data);
      // Default to the first day that still has a free slot
      setDateKey((current) => current || json.data.days.find((d) => d.slots.some((s) => s.available))?.date || '');
    } catch {
      setLoadError('We could not load available times. Please try again.');
    }
  }, []);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const days = useMemo(() => schedule?.days || [], [schedule]);
  const dayMap = useMemo(() => Object.fromEntries(days.map((d) => [d.date, d])), [days]);
  const weeks = useMemo(() => buildWeeks(days), [days]);
  const selectedDay = dayMap[dateKey];

  const slotGroups = useMemo(() => {
    const slots = selectedDay?.slots || [];
    return [
      { label: 'Morning', slots: slots.filter((s) => istHour(s.start) < 12) },
      { label: 'Afternoon', slots: slots.filter((s) => istHour(s.start) >= 12 && istHour(s.start) < 17) },
      { label: 'Evening', slots: slots.filter((s) => istHour(s.start) >= 17) }
    ].filter((g) => g.slots.length);
  }, [selectedDay]);

  // Prefill the name from the Google account
  useEffect(() => {
    if (user) setForm((f) => (f.name ? f : { ...f, name: fullNameOf(user) }));
  }, [user]);

  // Back from Google sign-in: return to the booking card on the date they had open
  useEffect(() => {
    if (!user || !schedule) return;
    const pending = takePendingDate();
    if (!pending) return;
    if (schedule.days.some((d) => d.date === pending)) setDateKey(pending);
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [user, schedule]);

  // Times are only bookable once signed in: remember the date and send them to Google
  const startSignIn = async () => {
    setSubmitError('');
    if (dateKey) savePendingDate(dateKey);
    setRedirecting(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      takePendingDate();
      setRedirecting(false);
      setSubmitError(err.message || 'Could not start Google sign-in. Please try again.');
    }
  };

  const locked = !user;

  const pickDate = (key) => {
    setDateKey(key);
    setSlot('');
  };

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const updatePhone = (e) => {
    if (!PHONE_ALLOWED.test(e.target.value)) return;
    setPhoneError('');
    update(e);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!normalizePhone(form.phoneNumber)) {
      setPhoneError(PHONE_ERROR);
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const token = await getAccessToken();
      if (!token) {
        setSubmitError('Your sign-in has expired. Please sign in again to book.');
        setStep('pick');
        return;
      }
      const res = await fetch(`${API_URL}/session-bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, slotStart: slot })
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setSubmitError(json.message || 'Something went wrong. Please try again.');
        if (res.status === 401) {
          setStep('pick');
          return;
        }
        // The slot was taken or expired: refresh times and send them back to pick again
        if (res.status === 409 || res.status === 400) {
          if (res.status === 409) {
            setSlot('');
            setStep('pick');
          }
          loadSlots();
        }
        return;
      }
      // Slot is held: pay for it on the checkout page
      navigate(`/checkout?booking=${json.data.id}`);
    } catch {
      setSubmitError('Could not reach the server. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({ ...emptyForm, name: fullNameOf(user) });
    setSlot('');
    setStep('pick');
    setSubmitError('');
    loadSlots();
  };

  const monthLabel = days.length
    ? [...new Set(days.map((d) => fmt(d.slots[0].start, { month: 'long', year: 'numeric' })))].join(' – ')
    : '';

  return (
    <section className="home-section booking" id="book-session" ref={sectionRef}>
      <div className="container">
        <div className="home-section-head booking-head" data-reveal>
          <span className="section-eyebrow">1:1 roadmap session{SESSION_HAS_DISCOUNT && ` · ${SESSION_DISCOUNT_PERCENT}% off`}</span>
          <h2>
            The most expensive course is <span className="text-gradient">the one you didn&apos;t need.</span>
          </h2>
          <p>
            Effort was never the problem. Direction was. Before you spend another rupee, spend 30 minutes with a
            senior engineer and walk away with a plan built for <em>you</em>.
          </p>
        </div>

        <div className="booking-grid">
          <div className="booking-story">
            <p className="booking-for" data-reveal>
              For <strong>students, freshers and early-career developers</strong> who have bought course after course
              and still don&apos;t know what to do next.
            </p>
            <div className="booking-receipt" data-reveal aria-label="A typical learner's course spending">
              <div className="booking-receipt-head">
                <span>Your last 12 months</span>
                <span>Paid</span>
              </div>
              <ul>
                {receipt.map((r, i) => (
                  <li key={r.item} style={{ '--i': i }}>
                    <span>
                      {r.item}
                      <small>{r.note}</small>
                    </span>
                    <span>₹{inr(r.cost)}</span>
                  </li>
                ))}
              </ul>
              <div className="booking-receipt-total">
                <span>Total spent</span>
                <strong><CountUp end={receiptTotal} duration={1800} prefix="₹" format={inr} /></strong>
              </div>
              <div className="booking-receipt-result">
                <span>Months spent</span>
                <strong>12</strong>
              </div>
              <div className="booking-receipt-result">
                <span>Offer letters</span>
                <strong>0</strong>
              </div>
              <span className="booking-receipt-stamp" aria-hidden="true">Sound familiar?</span>
            </div>

            <div className="booking-bridge" aria-hidden="true">
              <span><ScheduleOutlinedIcon sx={{ fontSize: 16 }} /> One 30-minute call · {formatInr(SESSION_PRICE_INR)}</span>
            </div>

            <div className="booking-plan" data-reveal style={{ '--i': 1 }}>
              <div className="booking-plan-head">
                <span>What you get on the call</span>
                <span className="booking-plan-badge">{formatInr(SESSION_PRICE_INR)}</span>
              </div>
              <ol>
                {plan.map((p, i) => (
                  <li key={p.step} style={{ '--i': i }}>
                    <span className="booking-plan-dot" />
                    <div>
                      <small>{p.step}</small>
                      <strong>{p.title}</strong>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="booking-plan-foot">
                <CheckIcon sx={{ fontSize: 16 }} /> Save months of guesswork and spend only on what closes your gap.
              </div>
            </div>
          </div>

          <div className="booking-card" data-reveal style={{ '--i': 1 }}>
            <div className="booking-card-head">
              <div>
                <strong>Talk to software architects</strong>
                <span>15+ years at product companies · have hired engineers</span>
              </div>
            </div>
            <div className="booking-card-meta">
              <span><ScheduleOutlinedIcon sx={{ fontSize: 16 }} /> 30 min</span>
              <span><VideocamOutlinedIcon sx={{ fontSize: 16 }} /> Video call</span>
              <span><EventAvailableOutlinedIcon sx={{ fontSize: 16 }} />
                {SESSION_HAS_DISCOUNT && <s className="booking-list-price">{formatInr(SESSION_LIST_PRICE_INR)}</s>} {formatInr(SESSION_PRICE_INR)}
              </span>
            </div>

            {step === 'done' ? (
              <div className="booking-done" role="status">
                <span className="booking-done-icon"><CheckIcon /></span>
                <h3>You&apos;re booked, {(form.name || fullNameOf(user)).split(' ')[0]}!</h3>
                <p className="booking-done-when">
                  {longDate(slot)} · {timeLabel(slot)} IST
                </p>
                {invited ? (
                  <p>
                    We&apos;ve sent a calendar invite with the Google Meet link to <strong>{user?.email}</strong>.
                    Come with your questions — we&apos;ll bring the plan.
                  </p>
                ) : (
                  <p>
                    A mentor will reach out on <strong>{user?.email}</strong> with the call link. Come with your
                    questions — we&apos;ll bring the plan.
                  </p>
                )}
                <div className="booking-done-actions">
                  {!invited && (
                    <a
                      className="btn-hero-primary"
                      href={googleCalendarLink(slot, schedule?.slotMinutes || 30)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Add to Google Calendar
                    </a>
                  )}
                  <button type="button" className="booking-link" onClick={reset}>Book another slot</button>
                </div>
              </div>
            ) : (
              <>
                <ol className="booking-steps" aria-label="Booking steps">
                  <li className={step === 'pick' ? 'active' : 'done'}><span>1</span> Pick a time</li>
                  <li className={step === 'details' ? 'active' : ''}><span>2</span> Your details</li>
                </ol>

                {step === 'pick' && (
                  <div className="booking-pick">
                    {loadError ? (
                      <div className="booking-state">
                        <p>{loadError}</p>
                        <button type="button" className="booking-link" onClick={loadSlots}>Retry</button>
                      </div>
                    ) : !schedule ? (
                      <div className="booking-state booking-skeleton" aria-busy="true">Loading available times…</div>
                    ) : (
                      <>
                        <div className="booking-cal" role="grid" aria-label="Choose a date">
                          <div className="booking-cal-head">
                            <strong>{monthLabel}</strong>
                            <span>All times in IST</span>
                          </div>
                          <div className="booking-cal-row booking-cal-weekdays" role="row">
                            {WEEKDAYS.map((w) => <span key={w} role="columnheader">{w}</span>)}
                          </div>
                          {weeks.map((week) => (
                            <div className="booking-cal-row" role="row" key={week[0]}>
                              {week.map((key) => {
                                const day = dayMap[key];
                                const free = day?.slots.filter((s) => s.available).length || 0;
                                const d = keyToDate(key);
                                return (
                                  <button
                                    key={key}
                                    type="button"
                                    role="gridcell"
                                    className={`booking-day ${key === dateKey ? 'selected' : ''}`}
                                    disabled={!free}
                                    aria-selected={key === dateKey}
                                    aria-label={`${d.toLocaleDateString('en-IN', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' })}${free ? `, ${free} slots free` : ', unavailable'}`}
                                    onClick={() => pickDate(key)}
                                  >
                                    {d.getUTCDate()}
                                    {free > 0 && <i aria-hidden="true" />}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </div>

                        <div className={`booking-times ${locked ? 'is-locked' : ''}`}>
                          <div className="booking-times-content" inert={locked} aria-hidden={locked}>
                            <div className="booking-slots">
                              {selectedDay ? (
                                <>
                                  <p className="booking-slots-day">{longDate(selectedDay.slots[0].start)}</p>
                                  {slotGroups.map((g) => (
                                    <div className="booking-slot-group" key={g.label}>
                                      <span>{g.label}</span>
                                      <div>
                                        {g.slots.map((s) => (
                                          <button
                                            key={s.start}
                                            type="button"
                                            className={`booking-slot ${slot === s.start ? 'selected' : ''}`}
                                            disabled={!s.available}
                                            aria-pressed={slot === s.start}
                                            onClick={() => setSlot(s.start)}
                                          >
                                            {timeLabel(s.start)}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <p className="booking-slots-empty">No open slots right now. Please check back soon.</p>
                              )}
                            </div>

                            {submitError && !locked && <p className="booking-error" role="alert">{submitError}</p>}

                            <button
                              type="button"
                              className="btn-hero-primary booking-next"
                              disabled={!slot}
                              onClick={() => { setSubmitError(''); setStep('details'); }}
                            >
                              {slot ? `Continue with ${timeLabel(slot)}` : 'Select a time to continue'}
                              <ArrowForwardIcon sx={{ fontSize: 18 }} />
                            </button>
                          </div>

                          {locked && (
                            <div className="booking-lock">
                              <span className="booking-lock-icon"><LockOutlinedIcon sx={{ fontSize: 20 }} /></span>
                              <strong>Sign in to pick a time</strong>
                              <p>We&apos;ll send your calendar invite and Google Meet link to your Google account.</p>
                              <button
                                type="button"
                                className="booking-google"
                                onClick={startSignIn}
                                disabled={authLoading || redirecting}
                              >
                                <GoogleMark />
                                {authLoading ? 'Checking sign-in…' : redirecting ? 'Opening Google…' : 'Continue with Google'}
                              </button>
                              {submitError && <p className="booking-error" role="alert">{submitError}</p>}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {step === 'details' && (
                  <form className="booking-form" onSubmit={submit}>
                    <button type="button" className="booking-chosen" onClick={() => setStep('pick')}>
                      <ArrowBackIcon sx={{ fontSize: 16 }} />
                      <span>{longDate(slot)} · {timeLabel(slot)} IST</span>
                      <em>Change</em>
                    </button>

                    <label>
                      Full name
                      <input name="name" value={form.name} onChange={update} required autoComplete="name" />
                    </label>
                    <div className="booking-form-row">
                      <label>
                        Email
                        <input type="email" value={user?.email || ''} readOnly title="From your Google account" />
                      </label>
                      <label>
                        Phone / WhatsApp
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={form.phoneNumber}
                          onChange={updatePhone}
                          onBlur={() => form.phoneNumber && !normalizePhone(form.phoneNumber) && setPhoneError(PHONE_ERROR)}
                          required
                          autoComplete="tel"
                          inputMode="tel"
                          maxLength={20}
                          placeholder="+91 98765 43210"
                          aria-invalid={Boolean(phoneError)}
                          aria-describedby={phoneError ? 'booking-phone-error' : undefined}
                        />
                        {phoneError && <span className="booking-field-error" id="booking-phone-error">{phoneError}</span>}
                      </label>
                    </div>
                    <label>
                      Where are you right now?
                      <select name="currentStage" value={form.currentStage} onChange={update} required>
                        <option value="" disabled>Select one</option>
                        {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </label>
                    <label>
                      What do you want from the next 6–12 months? <small>(optional)</small>
                      <textarea
                        name="goal"
                        rows={3}
                        value={form.goal}
                        onChange={update}
                        placeholder="e.g. Move from service to a product company as a backend engineer"
                      />
                    </label>

                    {submitError && <p className="booking-error" role="alert">{submitError}</p>}

                    <button type="submit" className="btn-hero-primary booking-next" disabled={submitting}>
                      {submitting ? 'Holding your slot…' : 'Book my session'}
                      {!submitting && <ArrowForwardIcon sx={{ fontSize: 18 }} />}
                    </button>
                    <p className="booking-fine">
                      {formatInr(SESSION_PRICE_INR)} for a 30-minute session
                      {SESSION_HAS_DISCOUNT && ` (${SESSION_DISCOUNT_PERCENT}% off ${formatInr(SESSION_LIST_PRICE_INR)})`}, paid securely
                      on the next step. Have a coupon? Apply it at checkout. Your slot is held for 15 minutes while you pay.
                    </p>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoadmapBooking;
